<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import { IRestaurant } from '@/interfaces/IRestaurant';
  import { Globals } from '@/modules/Globals';
  import { vueData } from '@/VueData';
  import { AlertType } from '@/interfaces/IAlert';
  import { useMenuV2 } from '@/composition/use-menu-v2';
  import { menuPagePath } from '@/domain/catalog/menu-links';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  // i18n in script context (script setup has no $t)
  const proxy = getCurrentInstance()!.proxy;

  // Store
  const restaurantStore = useRestaurantStore();
  const { isMenuV2Active } = useMenuV2();

  const categoriesPath = computed(() =>
    menuPagePath(
      restaurantStore.restaurant?.stringId ?? '',
      isMenuV2Active.value,
      isMenuV2Active.value ? {} : { category: 'new' }
    )
  );
  const itemsPath = computed(() =>
    menuPagePath(
      restaurantStore.restaurant?.stringId ?? '',
      isMenuV2Active.value
    )
  );

  // Props
  const props = defineProps<{
    itemCount: number;
    categoryCount: number;
  }>();

  // Computed
  const uploadsPath = computed(() => Globals.uploadsPath);
  const logoUrl = computed(() =>
    restaurantStore.restaurant?.logo
      ? `${uploadsPath.value}/${restaurantStore.restaurant.logo}`
      : 'assets/img/restaurant-logo.png'
  );

  const storeUrl = computed(
    () => `https://bistri.fi/${restaurantStore.restaurant?.stringId}`
  );

  const currentPlan = computed(() => {
    if (restaurantStore.planExpired)
      return { name: String(proxy.$t('dashboard.planExpired')), color: 'red' };
    if (restaurantStore.effectiveTier === 0)
      return { name: 'Lite', color: 'green' };

    const plan = Globals.plans.find(
      (p) =>
        p.tier === restaurantStore.effectiveTier &&
        ((!restaurantStore.restaurant.subTier && !p.subTier) ||
          p.subTier === restaurantStore.restaurant.subTier)
    );

    if (!plan) return { name: 'Unknown', color: 'grey' };

    return {
      name: plan.title,
      color: restaurantStore.effectiveTier === 1 ? 'orange' : '#ff6f00',
    };
  });

  // Methods
  const copyStoreUrl = () => {
    navigator.clipboard
      .writeText(storeUrl.value)
      .then(() => {
        vueData.addAlert(
          String(proxy.$t('dashboard.urlCopied')),
          AlertType.Success
        );
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
        vueData.addAlert(
          String(proxy.$t('dashboard.urlCopyError')),
          AlertType.Error
        );
      });
  };
</script>

<template>
  <v-card class="header-card" elevation="0">
    <v-card-text class="header-content">
      <div class="restaurant-info">
        <!-- Logo -->
        <div class="logo-container">
          <v-skeleton-loader
            v-if="!restaurantStore.restaurant"
            type="avatar"
            width="80"
            height="80"
            class="logo-skeleton"
          />
          <router-link
            v-else
            :to="`/${restaurantStore.restaurant?.stringId}/settings`"
            class="logo-link"
          >
            <v-img
              :src="logoUrl"
              class="restaurant-logo"
              width="80"
              height="80"
              cover
            />
          </router-link>
        </div>

        <!-- Restaurant Details -->
        <div class="restaurant-details">
          <v-skeleton-loader
            v-if="!restaurantStore.restaurant"
            type="heading"
            width="250"
            class="mb-2"
          />
          <h1 v-else class="restaurant-name">
            {{ restaurantStore.restaurant.name }}
          </h1>

          <div class="restaurant-meta">
            <v-skeleton-loader
              v-if="!restaurantStore.restaurant"
              type="text"
              width="200"
            />
            <div v-else class="store-url-container">
              <a :href="storeUrl" target="_blank" class="store-url">
                bistri.fi/{{ restaurantStore.restaurant.stringId }}
              </a>
              <v-btn icon small @click="copyStoreUrl" class="copy-btn">
                <v-icon small>mdi-content-copy</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Stats Summary -->
          <div class="stats-summary">
            <v-skeleton-loader
              v-if="!restaurantStore.restaurant"
              type="chip"
              width="150"
            />
            <div v-else class="stat-chips">
              <router-link
                :to="categoriesPath"
                class="stat-chip-link"
              >
                <v-chip small outlined class="stat-chip">
                  <v-icon small left>mdi-menu</v-icon>
                  {{ $t('dashboard.categoriesChip', { count: categoryCount }) }}
                </v-chip>
              </router-link>
              <router-link
                :to="itemsPath"
                class="stat-chip-link"
              >
                <v-chip small outlined class="stat-chip">
                  <v-icon small left>mdi-silverware</v-icon>
                  {{ $t('dashboard.itemsChip', { count: itemCount }) }}
                </v-chip>
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="header-actions">
        <v-skeleton-loader
          v-if="!restaurantStore.restaurant"
          type="button"
          width="100"
          class="mb-2"
        />
        <router-link
          v-else
          :to="`/${restaurantStore.restaurant.stringId}/settings?tab=plan`"
          class="plan-link"
        >
          <v-chip
            :color="currentPlan.color"
            text-color="white"
            class="plan-chip"
          >
            {{ currentPlan.name }}
          </v-chip>
        </router-link>

        <v-skeleton-loader
          v-if="!restaurantStore.restaurant"
          type="button"
          width="48"
        />
        <v-btn
          v-else
          icon
          large
          :to="`/${restaurantStore.restaurant.stringId}/settings`"
          class="settings-btn"
        >
          <v-icon>mdi-cog</v-icon>
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
  .header-card {
    @apply tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-neutral-100;

    .header-content {
      @apply tw-flex tw-items-center tw-justify-between tw-p-6;
    }

    .restaurant-info {
      @apply tw-flex tw-items-center tw-space-x-6;

      .logo-container {
        @apply tw-flex-shrink-0;

        .logo-skeleton {
          @apply tw-rounded-lg;
        }

        .logo-link {
          @apply tw-block tw-transition-transform tw-duration-200;

          &:hover {
            @apply tw-transform tw-scale-105;
          }

          .restaurant-logo {
            @apply tw-rounded-lg tw-shadow-md;
          }
        }
      }

      .restaurant-details {
        @apply tw-flex-grow;

        .restaurant-name {
          @apply tw-text-3xl tw-font-semibold tw-text-neutral-900 tw-mb-2;
        }

        .restaurant-meta {
          @apply tw-mb-3;

          .store-url-container {
            @apply tw-flex tw-items-center tw-space-x-2;

            .store-url {
              @apply tw-text-blue-600 hover:tw-text-blue-800 tw-transition-colors;
              @apply tw-text-sm tw-font-medium;
            }

            .copy-btn {
              @apply tw-opacity-60 hover:tw-opacity-100 tw-transition-opacity;
            }
          }
        }

        .stats-summary {
          .stat-chips {
            @apply tw-flex tw-items-center tw-space-x-2;

            .stat-chip-link {
              @apply tw-no-underline;

              .stat-chip {
                @apply tw-border-neutral-300;
                @apply tw-cursor-pointer tw-transition-all tw-duration-200;
              }
            }
          }
        }
      }
    }

    .header-actions {
      @apply tw-flex tw-items-center tw-space-x-3;

      .plan-link {
        @apply tw-no-underline;

        .plan-chip {
          @apply tw-font-medium tw-transition-transform tw-duration-200;

          &:hover {
            @apply tw-transform tw-scale-105;
          }
        }
      }

      .settings-btn {
        @apply tw-bg-neutral-100 hover:tw-bg-neutral-200 tw-transition-colors;
      }
    }
  }

  // Responsive adjustments
  @media (max-width: 768px) {
    .header-card {
      .header-content {
        @apply tw-flex-col tw-space-y-4;
      }

      .restaurant-info {
        @apply tw-w-full;

        .restaurant-details {
          .restaurant-name {
            @apply tw-text-2xl;
          }
        }
      }

      .header-actions {
        @apply tw-w-full tw-justify-end;
      }
    }
  }
</style>
